"use client";
import Slider from "react-slick";
import { useRef } from "react";
import { useRouter } from "next/navigation";
import { groupkebabCase } from "@/utils/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addFilter, filterItems } from "@/redux/features/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { emptyFilter } from "@/utils/enum";

const THEMES = [
  { bg: "#ece9ff", badge: "#ddd6ff", accent: "#5b4ee8" }, // lavender
  { bg: "#e2f4ec", badge: "#c9e9d8", accent: "#2f9d6e" }, // mint
  { bg: "#ffe9ec", badge: "#ffd4db", accent: "#e2566c" }, // peach
  { bg: "#fff4de", badge: "#ffe7b3", accent: "#d48a00" }, // golden
];

const offerings = [
  {
    no: "01",
    icon: "🏆",
    title: "Viva Sports",
    desc: "A prestigious inter-school tournament where students showcase their talent across multiple sports.",
    q: ["Sports"],
    filterBy: "filter",
  },
  {
    no: "02",
    icon: "🎤",
    title: "Junior Star Ensemble",
    desc: "An incredible platform for students to showcase their singing and dancing prowess.",
    q: ["Junior Star Ensemble"],
    filterBy: "filter",
  },
  {
    no: "03",
    icon: "💃",
    title: "Star Ensemble",
    desc: "An opportunity for students to show off their solo dancing abilities.",
    q: ["Star Ensemble"],
    filterBy: "filter",
  },
  {
    no: "04",
    icon: "🛠️",
    title: "Workshops and Competitions",
    desc: "A wide array of workshops and competitions for participants.",
    q: ["Workshop", "Competition"],
    filterBy: "filter",
  },
  {
    no: "05",
    icon: "👨‍👩‍👧‍👦",
    title: "Family Fiesta",
    desc: "A wide range of fun-filled activities that brings the family together and allows them to make memories that last a lifetime.",
    q: "family fiesta",
    filterBy: "group_name",
  },
  {
    no: "06",
    icon: "🏃‍♂️",
    title: "Vivathon",
    desc: "A one-of-a-kind marathon that brings the whole community together.",
    q: "vivathon",
    filterBy: "group_name",
  },
  {
    no: "07",
    icon: "🥇",
    title: "Junior Championship",
    desc: "Discover the stars of tomorrow as a number of tiny tots compete across a multitude of sporting disciplines.",
    q: "junior championship",
    filterBy: "group_name",
  },
];

const PopularSports = () => {
  const router = useRouter();
  const sliderRef = useRef<Slider | null>(null);
  const dispatch = useDispatch();
  const filter = useSelector(filterItems);
  const multiple = offerings.length > 1;

  const settings = {
    dots: false,
    arrows: false,
    infinite: multiple,
    speed: 500,
    autoplay: multiple,
    autoplaySpeed: 4500,
    slidesToShow: 4,
    slidesToScroll: 1,
    centerMode: multiple,
    centerPadding: "24px",
    pauseOnHover: true,
    dotsClass: "slick-dots hero-dots",
    responsive: [
      {
        breakpoint: 1400,
        settings: { slidesToShow: 3, centerPadding: "16px" },
      },
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2, centerPadding: "8px" },
      },
      {
        breakpoint: 550,
        settings: { slidesToShow: 1, centerPadding: "0px" },
      },
    ],
  };

  return (
    <>
      <style>{`
        .offer-section {
          position: relative;
          background: linear-gradient(135deg, #5b4ee8 0%, #3f41d1 100%);
          padding: 0 0 4.5rem;
          overflow: hidden;
        }
        /* curved white top edge (white above, dips into purple) */
        .offer-curve {
          display: block;
          width: 100%;
          height: 70px;
          margin-bottom: 1.5rem;
        }
        .offer-curve svg { display: block; width: 100%; height: 100%; }
        .offer-head {
          text-align: center;
          margin: 0.5rem auto 2.75rem;
          padding: 0 1.5rem;
        }
        .offer-title {
          font-size: 2.4rem;
          font-weight: 800;
          color: #fff;
          margin: 0;
          font-family: Graphik, sans-serif;
          letter-spacing: -0.01em;
        }
        .offer-sub {
          color: rgba(255,255,255,0.82);
          font-size: 1rem;
          margin: 0.65rem auto 0;
          max-width: 560px;
          font-family: Graphik, sans-serif;
        }
        .offer-slider-wrap {
          position: relative;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 70px; /* space for arrows */
        }
        .offer-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 48px;
          height: 48px;
          border: none;
          border-radius: 50%;
          // background: #fff;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 10;
          transition: all 0.25s ease;
        }
        .offer-arrow:hover {
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }
        .offer-slider-wrap {
          position: relative;
          padding: 0 60px;
        }

        .offer-arrow-left {
          left: 0;
        }

        .offer-arrow-right {
          right: 0;
        }
        .offer-slick .slick-slide > div { display: flex; padding: 0 12px; }
        .offer-slick .slick-list { overflow: hidden; padding: 0.5rem 0rem !important }
        .offer-card {
          position: relative;
          width: 100%;
          min-height: 290px;
          border-radius: 20px;
          padding: 1.5rem 1.4rem 1.6rem;
          display: flex;
          flex-direction: column;
          cursor: pointer;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          box-shadow: 0 10px 26px rgba(20, 18, 80, 0.18);
        }
        .offer-card:hover { transform: translateY(-6px); box-shadow: 0 18px 36px rgba(20, 18, 80, 0.28); }
        .offer-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1.6rem;
        }
        .offer-num {
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          border-radius: 12px;
          padding: 0.45rem 0.7rem;
          line-height: 1;
        }
        .offer-icon {
          font-size: 1.9rem;
          line-height: 1;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.12));
        }
        .offer-card-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: #1a1a2e;
          font-family: Graphik, sans-serif;
          line-height: 1.2;
          margin-bottom: 0.65rem;
        }
        .offer-card-desc {
          font-size: 0.9rem;
          color: #5a5a72;
          line-height: 1.55;
          font-family: Graphik, sans-serif;
          margin: 0;
        }
        .offer-redirect-arrow {
          position:absolute;
          right: 0;
          bottom: 0;
          transform: translate(-50%, -50%);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #3f41d1;
          border: 1.5px solid #3f41d1;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.2s;
        }
        .offer-card:hover .offer-redirect-arrow { transform: translate(-50%, -65%) }
        @media (max-width: 600px) {
          .offer-title { font-size: 1.8rem; }
        }
      `}</style>

      <section className="offer-section">
        <div className="offer-curve">
          <svg
            viewBox="0 0 1440 90"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0,0 H1440 V22 Q720,96 0,22 Z" fill="#ffffff" />
          </svg>
        </div>

        <div className="offer-head">
          <h2 className="offer-title">What we offer?</h2>
          <p className="offer-sub">
            Discover a diverse range of competitions across Sports, Music &
            Performing Arts designed to inspire talent, creativity, and
            confidence. Each experience encourages students to learn, compete,
            and grow.
          </p>
        </div>

        <div className="offer-slider-wrap">
          <button
            className="offer-arrow offer-arrow-left"
            onClick={() => sliderRef.current?.slickPrev()}
          >
            <ChevronLeft />
          </button>
          <Slider {...settings} ref={sliderRef} className="offer-slick">
            {offerings.map((o, i) => {
              const t = THEMES[i % THEMES.length];
              return (
                <div key={o.no}>
                  <div
                    className="offer-card"
                    style={{ background: t.bg }}
                    onClick={() => {
                      if (o.filterBy === "filter") {
                        dispatch(
                          addFilter({
                            ...emptyFilter,
                            eventsType: o.q,
                            eventSearchBox: "",
                          }),
                        );
                      } else if (o.filterBy === "group_name") {
                        dispatch(
                          addFilter({
                            ...emptyFilter,
                            eventsType: [],
                            eventSearchBox: o.q,
                          }),
                        );
                      }
                      router.push(`/events`);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="offer-card-top">
                      <span
                        className="offer-num"
                        style={{ background: t.badge, color: t.accent }}
                      >
                        {o.no}
                      </span>
                      <span className="offer-icon">{o.icon}</span>
                    </div>
                    <h3 className="offer-card-title">{o.title}</h3>
                    <p className="offer-card-desc">{o.desc}</p>
                    <span className="offer-redirect-arrow">
                      <i
                        className="bi bi-arrow-up-right"
                        style={{ fontSize: "14px", color: "#fff" }}
                      ></i>
                    </span>
                  </div>
                </div>
              );
            })}
          </Slider>
          <button
            className="offer-arrow offer-arrow-right"
            onClick={() => sliderRef.current?.slickNext()}
          >
            <ChevronRight />
          </button>
        </div>
      </section>
    </>
  );
};

export default PopularSports;
